import cloneDeep from 'clone-deep'
import { accountTypeWithBorrower, FieldOrigin, InputType, isCustomOrigin } from 'config'
import { accountTypeWithAllBorrower, AvailableBorrowerLabels } from 'pages/LoanApplication/config'
import { useEffect, useState } from 'react'
import { store } from 'reducers'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { Modal } from 'stories/components'
import { InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { workflowRestrictions } from '../Workflows/constants'
import { VisibleLogicByAccountTypes } from './VisibleLogicByAccountTypes'
import { spanOptions } from './VisiblePropertyModal/constants'

const sectionTypeOptions = {
  Borrower: 'Borrower',
  SubMenu: 'SubMenu',
}

const defaultInputs = (): Record<string, InputType> => {
  const { loanGlobalStatus } = store.getState()
  return {
    sectionType: {
      inputType: 'select',
      title: 'Type',
      options: sectionTypeOptions,
      hasDefaultOption: false,
      value: 'Borrower',
    },
    accountTypes: {
      inputType: 'multiselect',
      title: 'Visible by Account Type',
      options: accountTypeWithAllBorrower(),
      defaultSelected: false,
      value: {},
      sort: true,
    },
    visibleLoanTypes: {
      inputType: 'multiselect',
      title: 'Visible by Channel',
      options: getLoanTypeMapForSelect(),
      defaultSelected: true,
      value: {},
      sort: false,
    },
    visibleByBorrower: {
      inputType: 'multiselect',
      title: 'Visible for Borrower',
      options: AvailableBorrowerLabels(),
      defaultSelected: true,
      value: {},
      sort: false,
    },
    visibleByCondition: {
      inputType: 'multiselect',
      title: 'Visibility Conditions',
      options: workflowRestrictions,
      defaultSelected: false,
      value: {},
      sort: false,
    },
    lockByAccountTypes: {
      inputType: 'multiselect',
      title: 'Lock by Account Type',
      options: accountTypeWithBorrower(),
      defaultSelected: false,
      value: {},
      sort: false,
    },
    lockByLoanStatus: {
      inputType: 'multiselect',
      title: 'Lock by Loan Status',
      options: loanGlobalStatus,
      defaultSelected: false,
      value: {},
      sort: false,
    },
    visibleLogicByAccountTypes: {
      inputType: 'custom',
      title: 'Visible Logic By Account Types',
      value: [],
    },
    visibleLogic: {
      inputType: 'visibleLogic',
      title: 'Visibility by Fields Conditions',
      options: {
        hasCustomOrigin: true,
        hasOtherOrigin: true,
        hasNotVisible: true,
      },
    },
    numberOfGrid: {
      inputType: 'select',
      title: 'Number of Grid',
      options: spanOptions,
      hasDefaultOption: false,
      value: 2,
      visible: false,
    },
  }
}

export const VisibleSectionModal = ({
  fieldOrigin,
  data: defaultData,
  onClose,
  onOk: _onOk,
}: {
  fieldOrigin: FieldOrigin
  data: Record<string, any>
  onClose: Function
  onOk: Function
}) => {
  const [inputs, setInputs] = useState(defaultInputs())

  useEffect(() => {
    const newInputs = cloneDeep(inputs)
    Object.keys(inputs).forEach((key) => {
      if (key === 'sectionType') newInputs[key].value = defaultData[key] ?? 'Borrower'
      else if (key === 'numberOfGrid') newInputs[key].value = defaultData[key] ?? 2
      else newInputs[key].value = defaultData[key]
    })
    newInputs.visibleByBorrower.visible = ![FieldOrigin.PropertyInformation].includes(fieldOrigin)
    newInputs.numberOfGrid.visible = isCustomOrigin(fieldOrigin)
    setInputs(newInputs)
  }, [defaultData])

  const renderVisibleLogicByAccountTypes = (value: any, onChange: Function, input: InputType) => {
    return <VisibleLogicByAccountTypes value={value} onChange={onChange} input={input} />
  }

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onOk = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    let newData: any = cloneDeep(defaultData) || {}

    Object.keys(inputs).map((key) => {
      const input = newInputs[key]
      if (input.visible === false) return

      newInputs[key].error = InputValidate({ ...input, value: input.value })
      if (newInputs[key].error?.length) hasError = true
      newData[key] = newInputs[key].value
    })
    setInputs(newInputs)
    if (hasError) return

    _onOk(newData)
  }

  return (
    <Modal title="Visible Properties" isOpen onClose={() => onClose()} onOk={onOk}>
      <div className="w-120 h-120">
        {Object.keys(inputs).map((key) => {
          if (inputs[key].visible === false) return null
          if (key == 'visibleLogicByAccountTypes') (inputs[key] as any).render = renderVisibleLogicByAccountTypes

          return (
            <div key={key} className="mb-2">
              <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
